import {
  Dialog,
  DialogPanel,
  TransitionChild,
  DialogBackdrop,
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { getGradientClass } from "~/styles/styles";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import Link from "next/link";
import CustomButton from "../common/button";
import { SOCIAL_ICONS } from "~/content/contact";
import {
  serviceLinks,
  aboutUsLinks,
  contactUsLinks,
  Insights,
  Services,
  ContactUs,
  AboutUs,
  BookConsultation,
} from "~/content/navLinks";
import { type MenuProps } from "./flyoutMenus";
import { useActivePath } from "~/app/utils/navHelper";

type DrawerProps = {
  isOpen?: boolean;
  onClose?: any;
  onClick?: () => void;
};

type MenuGroupProps = {
  name: string,
  items: MenuProps[]
}

const NavDrawer = ({ isOpen, onClose, onClick }: DrawerProps) => {
  const checkActivePath = useActivePath();

  const backgroundColour = `${getGradientClass("v-l-to-d-two-colours")}`;

  const MenuGroup = ({ name, items }: MenuGroupProps) => {
    return (
      <Popover>
        {({ close }) => (
          <>
            <PopoverButton
              className={`flex w-full justify-between outline-none hover:text-orange`}
            >
              {name}
              <ArrowRightIcon className="size-6" />
            </PopoverButton>

            <Transition
              enter="transition duration-200 ease-out"
              enterFrom="-translate-x-full opacity-0"
              enterTo="translate-x-0 opacity-100"
              leave="transition duration-200 ease-out"
              leaveFrom="translate-x-0 opacity-100"
              leaveTo="-translate-x-full opacity-0"
            >
              <PopoverPanel
                className={`absolute left-0 top-0 z-20 flex h-[100vh] w-full flex-col justify-center p-6 ${backgroundColour}`}
              >
                <button
                  type="button"
                  onClick={() => {
                    close();
                  }}
                  className="absolute top-0 mt-[22px] rounded-md py-4 pr-5 text-white hover:text-green-light"
                >
                  <ArrowLeftIcon className="size-8 md:size-9" />
                </button>

                <div className="">
                  {items?.map((item) => (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={`block p-2 hover:text-green-light focus:text-green-light ${
                        checkActivePath(item.href, true)
                          ? "text-green"
                          : "text-white"
                      }`}
                      onClick={onClick}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              </PopoverPanel>
            </Transition>
          </>
        )}
      </Popover>
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-40">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black bg-opacity-65 transition-opacity duration-300 ease-in-out data-[closed]:opacity-0"
      />
      {/* overlay */}
      <div className="fixed inset-0 overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
            <DialogPanel
              transition
              className="max pointer-events-auto relative w-screen max-w-xs transform transition duration-300 ease-in-out data-[closed]:-translate-x-full xs:max-w-sm"
            >
              <TransitionChild>
                <div className="absolute left-0 top-0 z-10 mt-[22px] flex px-5 py-4 duration-300 ease-in-out data-[closed]:opacity-0">
                  <span className="sr-only">Close panel</span>
                  <button
                    type="button"
                    onClick={onClick}
                    className="relative rounded-md text-white hover:text-green-light"
                  >
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-8 md:size-10"
                    />
                  </button>
                </div>
              </TransitionChild>

              <div
                className={`relative flex h-full flex-1 flex-col overflow-y-scroll p-6 shadow-xl ${backgroundColour}`}
              >
                {/* menu items */}
                <div className="my-16 flex h-full flex-col justify-center gap-8 text-lg text-white">
                  <div className="group flex flex-col gap-5">
                    <MenuGroup name={Services.name} items={serviceLinks}  />
                    <Link
                      href={Insights.href}
                      className="leading-6 hover:text-orange"
                      onClick={onClick}
                    >
                      {Insights.name}
                    </Link>
                    <MenuGroup name={AboutUs.name} items={aboutUsLinks} />
                    <MenuGroup name={ContactUs.name} items={contactUsLinks} />
                  </div>
                  <CustomButton
                    href={BookConsultation.href}
                    className="w-fit text-sm ml:text-base max:text-xl"
                    onClick={onClick}
                  >
                    Book a consultation
                  </CustomButton>
                </div>

                {/* social icons */}
                <div className="flex gap-2 sm:order-2">
                  <SOCIAL_ICONS onClick={onClick} />
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default NavDrawer;
